// SPDX-License-Identifier: AGPL-3.0-or-later

import { createBrowserRouter, Navigate, type RouteObject } from 'react-router';
import { App } from './App.js';
import { ProtectedShell } from './components/ProtectedShell.js';
import { LoginRoute } from './routes/auth/LoginRoute.js';
import { SignupRoute } from './routes/auth/SignupRoute.js';
import { AcceptInviteRoute } from './routes/auth/AcceptInviteRoute.js';
import { ForgotPasswordRoute } from './routes/auth/ForgotPasswordRoute.js';
import { ResetPasswordRoute } from './routes/auth/ResetPasswordRoute.js';
import { OrgSettingsRoute } from './routes/settings/OrgSettingsRoute.js';
import { TeamRoute } from './routes/settings/TeamRoute.js';
import { UserProfileSettingsRoute } from './routes/settings/UserProfileSettingsRoute.js';
import { SettingsLayout } from './routes/settings/SettingsLayout.js';
import { DashboardRoute } from './routes/dashboard/DashboardRoute.js';
import { BuilderRoute } from './routes/builder/BuilderRoute.js';
import { PreviewRoute } from './routes/preview/PreviewRoute.js';
import { ResponseDetailRoute } from './routes/responses/ResponseDetailRoute.js';
import { ResponsesListRoute } from './routes/responses/ResponsesListRoute.js';
import { IntakeRoute } from './routes/intake/IntakeRoute.js';
import { DesignSystemRoute } from './routes/design-system/DesignSystemRoute.js';
import { FormsRoute } from './routes/forms/FormsRoute.js';
import { TemplatesRoute } from './routes/templates/TemplatesRoute.js';
import { AutofillListRoute } from './routes/autofill/AutofillListRoute.js';
import { AutofillCreateRoute } from './routes/autofill/AutofillCreateRoute.js';
import { AutofillReviewRoute } from './routes/autofill/AutofillReviewRoute.js';
import { AutofillPrintRoute } from './routes/autofill/AutofillPrintRoute.js';
import { NotFoundRoute } from './components/NotFoundRoute.js';

const routes: RouteObject[] = [
  {
    element: <App />,
    errorElement: <NotFoundRoute />,
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      { path: '/login', element: <LoginRoute /> },
      { path: '/signup', element: <SignupRoute /> },
      { path: '/forgot-password', element: <ForgotPasswordRoute /> },
      { path: '/reset-password', element: <ResetPasswordRoute /> },
      { path: '/accept-invite/:token', element: <AcceptInviteRoute /> },
      { path: '/intake/:token', element: <IntakeRoute /> },
      {
        element: <ProtectedShell />,
        children: [
          { path: '/dashboard', element: <DashboardRoute /> },
          { path: '/forms', element: <FormsRoute /> },
          { path: '/templates', element: <TemplatesRoute /> },
          { path: '/questionnaires', element: <Navigate to="/dashboard" replace /> },
          { path: '/builder', element: <BuilderRoute /> },
          { path: '/builder/:id', element: <BuilderRoute /> },
          { path: '/preview/:id', element: <PreviewRoute /> },
          { path: '/responses', element: <ResponsesListRoute /> },
          { path: '/responses/:id', element: <ResponseDetailRoute /> },
          { path: '/autofill', element: <AutofillListRoute /> },
          { path: '/autofill/new', element: <AutofillCreateRoute /> },
          { path: '/autofill/:id/preview', element: <AutofillReviewRoute stage="admin" /> },
          { path: '/autofill/:id/approve', element: <AutofillReviewRoute stage="doctor" /> },
          { path: '/autofill/:id/print', element: <AutofillPrintRoute /> },
          { path: '/design-system', element: <Navigate to="/settings/design-system" replace /> },
          {
            element: <SettingsLayout />,
            children: [
              { path: '/settings', element: <UserProfileSettingsRoute /> },
              { path: '/settings/organization', element: <OrgSettingsRoute /> },
              { path: '/settings/team', element: <TeamRoute /> },
              { path: '/settings/design-system', element: <DesignSystemRoute /> },
            ],
          },
        ],
      },
      { path: '*', element: <NotFoundRoute /> },
    ],
  },
];

export const router: ReturnType<typeof createBrowserRouter> = createBrowserRouter(routes);
